/* eslint-disable max-len*/
import { ReactElement, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { SingleValue } from 'react-select';
import { appRoutePaths } from 'src/services/route.service';
import { DataEntryMode } from 'src/types/DataEntryMode';
import ActionButton from '../../../../components/ActionButton/ActionButton';
import StyledSingleDropdown from '../../../../components/StyledSingleDropdown/StyledSingleDropdown';
import { useAppDispatch, useAppSelector } from '../../../../redux/hooks';
import {
  selectSchoolYear,
  selectStudentInterventionListItems,
  selectStudentInterventionLoadingListItems,
  selectStudentInterventionLoadingSchoolYears,
  selectStudentInterventionSelectedSchoolYearOption,
  selectStudentInterventionsSchoolYears,
  setSchoolYearFilter,
  setStudentInterventionDataEntryMode,
  setStudentInterventionSchoolYearOption,
} from '../../../../redux/slices/studentInterventionSlice';
import {
  fetchStudentInterventionListItems,
  fetchStudentInterventionsSchoolYears,
} from '../../../../redux/thunks/studentInterventionThunks';
import { TooltipAnchor } from '../../../../services/app.service';
import { abortPromiseOnUnmount } from '../../../../services/base.service';
import { canUserAccessRoute } from '../../../../services/security.service';
import { FormDropdownSelectOption } from '../../../../types/propTypes/FormDropdownPropTypes';
import { ReactSelectOption } from '../../../../types/ReactSelectOption';
import { StudentInterventionListItemModel } from '../../../../types/StudentInterventionListItemModel';
import { isStateNonAdmin } from '../../../../utilities/userUtilities';
import StudentPage from '../StudentPage';
import './StudentInterventionList.css';

const StudentInterventionList = (): ReactElement => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const schoolYear = useAppSelector(selectSchoolYear);
  const isLoadingListItems = useAppSelector(
    selectStudentInterventionLoadingListItems
  );
  const isLoadingSchoolYears = useAppSelector(
    selectStudentInterventionLoadingSchoolYears
  );
  const isLoading = isLoadingListItems || isLoadingSchoolYears;
  const listItems: StudentInterventionListItemModel[] = useAppSelector(
    selectStudentInterventionListItems
  );

  const schoolYearOptions = useAppSelector(
    selectStudentInterventionsSchoolYears
  );
  const selectedSchoolYearOption = useAppSelector(
    selectStudentInterventionSelectedSchoolYearOption
  );
  // Use SelectOption for options (value/text), ReactSelectOption for selected value (value/label)
  const convertedSchoolYearOptions = schoolYearOptions.map((option) => ({
    value: option.value,
    text: option.text,
  }));
  let selectedOption: FormDropdownSelectOption | null = null;
  let currentSchoolYear: string | null = null;
  if (schoolYearOptions && schoolYearOptions.length > 0) {
    //eslint-disable-next-line @typescript-eslint/no-unused-vars
    selectedOption = convertedSchoolYearOptions[0].text
      ? { value: schoolYear, label: convertedSchoolYearOptions[0].text }
      : {
          value: convertedSchoolYearOptions[0].value,
          label: convertedSchoolYearOptions[0].text,
        };
    currentSchoolYear = convertedSchoolYearOptions[0].value;
  }
  useEffect(() => {
    if (schoolYear === '' && convertedSchoolYearOptions.length > 0) {
      const defaultOption = convertedSchoolYearOptions[0];
      dispatch(setSchoolYearFilter(defaultOption.value));
      dispatch(
        setStudentInterventionSchoolYearOption({
          value: defaultOption.value,
          label: defaultOption.text,
        })
      );
    }
  }, [dispatch, schoolYear, convertedSchoolYearOptions]);

  useEffect(() => {
    let promise: unknown;
    if (schoolYear) {
      promise = dispatch(fetchStudentInterventionListItems(schoolYear));
    }
    return () => {
      abortPromiseOnUnmount(promise);
    };
  }, [dispatch, schoolYear]);

  useEffect(() => {
    const promise: unknown = dispatch(fetchStudentInterventionsSchoolYears());
    return () => {
      abortPromiseOnUnmount(promise);
    };
  }, [dispatch]);

  const handleOnChange = async (
    option: FormDropdownSelectOption
  ): Promise<void> => {
    const selectedValue = (option as SingleValue<ReactSelectOption>)?.value;
    const selectedLabel = (option as SingleValue<ReactSelectOption>)?.label;
    if (selectedValue && selectedLabel) {
      dispatch(setSchoolYearFilter(selectedValue));
      dispatch(
        setStudentInterventionSchoolYearOption({
          value: selectedValue,
          label: selectedLabel,
        })
      );
    }
  };

  const handleEdit = (studentInterventionId: string): void => {
    navigate(
      appRoutePaths.StudentInterventionInput(studentInterventionId, schoolYear)
    );
  };

  const handleNew = (): void => {
    dispatch(setStudentInterventionDataEntryMode(DataEntryMode.NEW));
    navigate(appRoutePaths.StudentInterventionInput('new', schoolYear));
  };

  const canAddNew = canUserAccessRoute(
    appRoutePaths.StudentInterventionInput()
  );

  return (
    <StudentPage
      pageClass="data-input--student--student-intervention--list"
      isLoading={isLoading}
      loadingDataId={'student-intervention-list-loader'}
    >
      <div className="content-outer-container">
        <div className="content-inner-container">
          <div
            className={
              isStateNonAdmin()
                ? 'school-year-selection-container school-year-padding'
                : 'school-year-selection-container'
            }
          >
            <StyledSingleDropdown
              fieldName="schoolYear"
              options={convertedSchoolYearOptions}
              onChange={handleOnChange}
              value={
                selectedSchoolYearOption.value
                  ? {
                      value: selectedSchoolYearOption.value,
                      label: selectedSchoolYearOption.label,
                    }
                  : null
              }
              isSearchable={false}
            />
          </div>
          <div className="subheading-and-button-container">
            <h4 data-testid={'component-heading-text'}>Student Intervention</h4>
            {currentSchoolYear && canAddNew && !isStateNonAdmin() && (
              <ActionButton
                onClick={handleNew}
                classes="no-wrap-text new-button"
                tooltipText="Add New Student Intervention"
              >
                <>Add New</>
              </ActionButton>
            )}
          </div>
          <div className="table-container">
            <table cellSpacing={0} className="page-view-table">
              <thead>
                <tr>
                  <th>Last Name</th>
                  <th>First Name</th>
                  <th>MSIS ID</th>
                  <th>School</th>
                  <th>Grade</th>
                  <th>Referral</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {listItems?.length > 0 &&
                  listItems.map((studentIntervention) => (
                    <tr
                      key={`${studentIntervention.id}`}
                      data-testid={studentIntervention.id}
                    >
                      <td>{studentIntervention.studentLastOrSurname}</td>
                      <td>{studentIntervention.studentFirstName}</td>
                      <td>{studentIntervention.studentIdentifierState}</td>
                      <td>{studentIntervention.schoolName}</td>
                      <td>{studentIntervention.grade}</td>
                      <td>
                        {studentIntervention.interventionCriteria.includes(
                          '*6 Referral Student'
                        )
                          ? 'Y'
                          : 'N'}
                      </td>
                      <td>
                        <div className="action--column">
                          <button
                            data-testid={`edit-button-${studentIntervention.id}`}
                            aria-label="Edit"
                            onClick={() => handleEdit(studentIntervention.id)}
                            data-tooltip-id={TooltipAnchor}
                            data-tooltip-content={'View/Edit Student'}
                          >
                            <img src={'/images/icon-edit.svg'} />
                          </button>
                        </div>
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </StudentPage>
  );
};

export default StudentInterventionList;
